import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { clinicAPI } from "../../../services/api";
import {
  showSuccessToast,
  showErrorToast,
} from "../../../utils/notificationUtils";

const SearchInviteTab = ({ clinicId, clinicCountryId, onSuccess, onClose }) => {
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [sendingId, setSendingId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);

  const ITEMS_PER_PAGE = 6;

  const searchVets = useCallback(async (searchQuery, page = 1) => {
    setIsSearching(true);
    setSearchError("");

    try {
      const params = {
        not_in_clinic: 1,
        per_page: ITEMS_PER_PAGE,
        page,
      };
      if (clinicCountryId) {
        params.country = clinicCountryId;
      }
      if (searchQuery.trim().length >= 2) {
        params.name = searchQuery.trim();
      }

      const response = await clinicAPI.getVeterinarians(params);
      const vets = response?.data?.data || [];
      const pagination = response?.data?.pagination || {};

      setResults(vets);
      setCurrentPage(pagination.current_page || 1);
      setLastPage(pagination.last_page || 1);
      setTotal(pagination.total || vets.length);

      if (vets.length === 0) {
        setSearchError(t("manageTeam.invite.noResults", "No veterinarians found"));
      }
    } catch (error) {
      setResults([]);
      setLastPage(1);
      setTotal(0);
      setSearchError(error?.response?.data?.message || t("manageTeam.errors.searchFailed"));
    } finally {
      setIsSearching(false);
    }
  }, [clinicCountryId, t]);

  // Load instantly on mount
  useEffect(() => {
    searchVets("", 1);
  }, [searchVets]);

  // Search as user types
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchVets(query, 1);
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [query, searchVets]);

  const handlePageChange = (page) => {
    if (page < 1 || page === currentPage || page > lastPage) return;
    searchVets(query, page);
  };

  const handleInvite = async (vet) => {
    if (!clinicId) {
      showErrorToast(t("manageTeam.errors.missingClinic"), { title: t("manageTeam.toasts.setupRequired.title"), duration: 5000 });
      return;
    }

    setSendingId(vet.user_id);
    try {
      const response = await clinicAPI.inviteExistingDoctor({
        clinic_id: clinicId,
        doctor_id: vet.user_id,
        message: t("manageTeam.invite.defaultMessage"),
      });

      if (response?.data?.status === "success") {
        showSuccessToast(
          t("manageTeam.toasts.inviteSuccess.body", { name: vet.full_name || vet.name }),
          { title: t("manageTeam.toasts.inviteSuccess.title"), duration: 4000 }
        );
        onSuccess?.();
      } else {
        throw new Error(response?.data?.message || t("manageTeam.errors.inviteFailed"));
      }
    } catch (error) {
      const message = error?.response?.data?.message || error.message || t("manageTeam.errors.inviteFailed");
      showErrorToast(message, { title: t("manageTeam.toasts.inviteFailed.title"), duration: 5000 });
    } finally {
      setSendingId(null);
    }
  };

  const renderPagination = () => {
    const totalPages = lastPage;
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="team-search-pagination" dir={i18n.dir()} lang={i18n.language}>
        {totalPages > 1 && (
          <>
            <button
              type="button"
              className="team-search-page-btn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || isSearching}
            >
              ‹
            </button>
            {startPage > 1 && (
              <>
                <button type="button" className="team-search-page-num" onClick={() => handlePageChange(1)}>1</button>
                {startPage > 2 && <span className="team-search-page-dots">…</span>}
              </>
            )}
            {pages.map((page) => (
              <button
                key={page}
                type="button"
                className={`team-search-page-num ${currentPage === page ? "active" : ""}`}
                onClick={() => handlePageChange(page)}
                disabled={isSearching}
              >
                {page}
              </button>
            ))}
            {endPage < totalPages && (
              <>
                {endPage < totalPages - 1 && <span className="team-search-page-dots">…</span>}
                <button type="button" className="team-search-page-num" onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
              </>
            )}
            <button
              type="button"
              className="team-search-page-btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || isSearching}
            >
              ›
            </button>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="team-search-invite" dir={i18n.dir()} lang={i18n.language}>
      <p style={{ color: "#64748b", fontSize: "14px", marginBottom: "10px", lineHeight: "1.5" }}>
        {t("manageTeam.invite.searchDescription", "Search for registered veterinarians in your country who are not currently in a clinic.")}
      </p>
      <div className="team-search-wrapper" style={{ maxWidth: "100%" }}>
        <span className="team-search-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
        </span>
        <input
          type="text"
          className="team-search"
          placeholder={t("manageTeam.invite.searchPlaceholder", "Search by name")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div className="team-search-results">
        {isSearching ? (
          <div className="team-search-empty">
            <div className="team-spinner" style={{ margin: "0 auto 12px" }} />
            {t("manageTeam.invite.searching", "Searching...")}
          </div>
        ) : results.length === 0 ? (
          <div className="team-search-empty">{searchError || t("manageTeam.invite.noResults", "No veterinarians found")}</div>
        ) : (
          results.map((vet) => (
            <div key={vet.user_id} className="team-search-result">
              <img
                src={vet.profile_image_url || "/images/doctor.png"}
                alt={vet.full_name || vet.name}
                className="team-search-avatar"
                onError={(e) => (e.target.src = "/images/doctor.png")}
              />
              <div className="team-search-info">
                <div className="team-search-name">{vet.full_name || vet.name}</div>
                <div className="team-search-meta">
                  {vet.email || t(`manageTeam.roles.${vet.user_type}`, { defaultValue: vet.user_type })}
                </div>
              </div>
              <button
                type="button"
                className="team-btn team-btn-primary team-search-add"
                onClick={() => handleInvite(vet)}
                disabled={sendingId === vet.user_id}
              >
                {sendingId === vet.user_id ? (
                  <span className="team-spinner" style={{ width: "14px", height: "14px", borderWidth: "2px" }} />
                ) : (
                  t("manageTeam.invite.addToClinic", "Invite")
                )}
              </button>
            </div>
          ))
        )}
        {results.length > 0 && results.length < ITEMS_PER_PAGE &&
          Array.from({ length: ITEMS_PER_PAGE - results.length }).map((_, i) => (
            <div key={`placeholder-${i}`} className="team-search-placeholder" aria-hidden="true" />
          ))}
      </div>

      {renderPagination()}
    </div>
  );
};

export default SearchInviteTab;
